import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ValidationError } from "@curio/client/errors";
import { isAuthError } from "@/lib/curio";
import { NotificationType } from "@context/NotificationProvider";
import { markCurioHandledError } from "@/utils";

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof ValidationError) {
    return error.detail.join("\n");
  }
  return error instanceof Error && error.message ? error.message : undefined;
};

type MutationMessages = { msgErro?: string; msgErroFallback?: string };

// useCurioMutation: msgErro/msgErroFallback flat. useHookMutation: dentro de options (params não é objeto livre).
const getMutationMessages = (variables: unknown): MutationMessages => {
  const vars = variables as (MutationMessages & { options?: MutationMessages }) | undefined;
  return {
    msgErro: vars?.msgErro ?? vars?.options?.msgErro,
    msgErroFallback: vars?.msgErroFallback ?? vars?.options?.msgErroFallback
  };
};

export const createQueryClient = (setNotification: (notification: NotificationType) => void) => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true
      },
      mutations: {
        retry: false,
        cacheTime: 3 * 60 * 1000
      }
    },
    queryCache: new QueryCache({
      onError: (error) => {
        markCurioHandledError(error);
        const message = getErrorMessage(error) ?? "Ocorreu um erro ao buscar os dados.";
        setNotification({ message, type: "error", isAuthError: isAuthError(error) });
      }
    }),
    // Única fonte de notif de erro de mutation — hooks não tratam onError, senão duplica.
    mutationCache: new MutationCache({
      onError: (error, variables) => {
        markCurioHandledError(error);
        const { msgErro, msgErroFallback } = getMutationMessages(variables);
        const message =
          msgErro ?? getErrorMessage(error) ?? msgErroFallback ?? "Ocorreu um erro ao processar a requisição.";
        setNotification({ message, type: "error", isAuthError: isAuthError(error) });
      }
    })
  });
};

export default createQueryClient;
